import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import useQueryParams from "../../hooks/useQueryParams";
import useUpdateSearchParams from "../../hooks/useUpdateSearchParams";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import Badge from "@mui/material/Badge";
import CircularProgress from "@mui/material/CircularProgress";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddIcon from "@mui/icons-material/Add";
import AddBoxIcon from "@mui/icons-material/AddBox";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ImageIcon from "@mui/icons-material/Image";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import {
  Description as ExcelIcon,
  FileDownload as ExportIcon,
  Build as ToolsIcon,
} from "@mui/icons-material";
import { usePopupState, bindTrigger, bindMenu } from "material-ui-popup-state/hooks";
import ConfirmRemoveAssetsModal from "./ConfirmRemoveAssetsModal";
import BulkInventoryMoveModal from "./BulkInventoryMoveModal";
import BulkAddToCartModal from "./bulk-actions/BulkAddToCartModal";
import BulkAddToOrderModal from "./bulk-actions/BulkAddToOrderModal";
import BulkTransferModal from "./bulk-actions/BulkTransferModal";
import BulkAddToInternalWorkOrderModal from "./bulk-actions/BulkAddToInternalWorkOrderModal";
import BulkImageUploadModal from "./bulk-actions/BulkImageUploadModal";
import BulkImageRemovalModal from "./bulk-actions/BulkImageRemovalModal";
import OnsiteCaptureWizard from "./OnsiteCaptureWizard/index";
import useNotification from "../../hooks/notification";
import { usePermissions } from "../../contexts/permissions";
import useAuth from "../../hooks/useAuth";
import useRoles from "../../hooks/user/useRoles";
import useMutation from "../../hooks/useMutation";
import {
  EXPORT_EXCEL_DATA_MUTATION,
  DOWNLOAD_ASSETS_IMAGES_MUTATION,
  UPDATE_USER_MUTATION,
} from "../../constants/graphql/mutations";
import { useBanner } from "../../contexts/banner";

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < breakpoint);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [breakpoint]);

  return isMobile;
};

const NestedMenuItem = ({ label, icon, children, popupId, activeSubmenu, setActiveSubmenu, disabled }) => {
  const isMobile = useIsMobile();
  const [openToLeft, setOpenToLeft] = useState(false);
  const menuItemRef = useRef(null);
  const closeTimeout = useRef(null);
  const isOpen = activeSubmenu === popupId;

  useEffect(() => () => clearTimeout(closeTimeout.current), []);

  const calcDirection = useCallback(() => {
    if (!menuItemRef.current) return;
    const { right } = menuItemRef.current.getBoundingClientRect();
    setOpenToLeft(window.innerWidth - right < 250);
  }, []);

  const open = useCallback(() => {
    clearTimeout(closeTimeout.current);
    calcDirection();
    setActiveSubmenu(popupId);
  }, [calcDirection, popupId, setActiveSubmenu]);

  const scheduleClose = useCallback(() => {
    closeTimeout.current = setTimeout(() => {
      setActiveSubmenu(prev => (prev === popupId ? null : prev));
    }, 200);
  }, [popupId, setActiveSubmenu]);

  const cancelClose = useCallback(() => clearTimeout(closeTimeout.current), []);

  return (
    <>
      <MenuItem
        ref={menuItemRef}
        onMouseEnter={!isMobile ? open : undefined}
        onMouseLeave={!isMobile ? scheduleClose : undefined}
        onClick={isMobile ? open : undefined}
        disabled={disabled}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box display="flex" alignItems="center">
          {icon && <Box mr={1}>{icon}</Box>}
          <ListItemText primary={label} />
        </Box>
        <ChevronRightIcon fontSize="small" />
      </MenuItem>
      <Menu
        open={isOpen}
        anchorEl={menuItemRef.current}
        onClose={() => setActiveSubmenu(null)}
        style={{ pointerEvents: "none" }}
        MenuListProps={{
          style: { pointerEvents: "auto" },
          onMouseEnter: cancelClose,
          onMouseLeave: scheduleClose,
        }}
        anchorOrigin={{ vertical: "top", horizontal: openToLeft ? "left" : "right" }}
        transformOrigin={{ vertical: "top", horizontal: openToLeft ? "right" : "left" }}
      >
        {children}
      </Menu>
    </>
  );
};

export default function BulkActions({
  label = "Bulk Actions",
  selectedIds,
  totalCount,
  onCompleteBulk,
  refetch,
  setDisabledScroll,
  bulkRemoveAssets,
  currentTableSettings,
  clientId,
  projectId,
  currentProject,
}) {
  const { user } = useAuth();
  const { can } = usePermissions();
  const { notifyError } = useNotification();
  const [exportExcelData] = useMutation(EXPORT_EXCEL_DATA_MUTATION);
  const [downloadAssetsImages] = useMutation(DOWNLOAD_ASSETS_IMAGES_MUTATION);
  const [updateUser] = useMutation(UPDATE_USER_MUTATION);
  const { displayBanner } = useBanner();
  const navigate = useNavigate();
  const queryParams = useQueryParams();
  const updateSearchParams = useUpdateSearchParams();

  const [isLoading, setIsLoading] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);

  const onsiteWizardParam = queryParams.get("onsiteWizard");
  const isOnsiteWizardOpen = !!onsiteWizardParam;
  const onsiteWizardIds = isOnsiteWizardOpen ? onsiteWizardParam.split(",") : [];

  const activeModal = queryParams.get("bulkModal");
  const bulkIdsParam = queryParams.get("bulkIds") || "";
  const bulkSelectedIds = useMemo(
    () => (selectedIds.length ? selectedIds : bulkIdsParam.split(",").filter(Boolean)),
    [selectedIds, bulkIdsParam]
  );

  const setActiveModal = useCallback(
    modalName =>
      updateSearchParams(
        modalName
          ? { bulkModal: modalName, bulkIds: bulkSelectedIds.join(",") }
          : { bulkModal: null, bulkIds: null }
      ),
    [updateSearchParams, bulkSelectedIds]
  );

  const isSelected = selectedIds.length > 0;
  const selectedCount = selectedIds.length || totalCount;

  const { settings } = user.company;

  const isAdminManagerClient = useRoles(["admin", "manager", "client", "inventory"]);
  const isAdmin = useRoles(["admin"]);
  const canWriteAssets = can("assets.write");
  const canRemoveAssets = can("assets.remove");
  const isExtendedCheckout =
    settings.wizard_mode || settings.simple_checkout_for_contract_warehouse;
  const showBulkOptions =
    isAdminManagerClient && settings.warehouse && currentProject?.storageEnabled;
  const isOnsiteCaptureToWarehouse = currentProject?.settings?.onsite_capture_to_warehouse_storage;

  const popupState = usePopupState({ variant: "popover", popupId: "bulk-actions-menu" });

  const handleBulkRemove = () => {
    setDisabledScroll(true);
    setIsLoading(true);

    displayBanner({ message: "Processing...", type: "bulk-remove-assets" });

    bulkRemoveAssets({
      variables: { ids: bulkSelectedIds },
      onSuccess: () => {
        onCompleteBulk();
        setDisabledScroll(false);
        setIsLoading(false);
        setActiveModal(null);
      },
      onFailure: errors => {
        notifyError(errors);
        setDisabledScroll(false);
        setIsLoading(false);
        setActiveModal(null);
      },
    });
  };

  const handleExportExcel = async () => {
    try {
      // First, save the current table settings to ensure backend has the latest state
      if (currentTableSettings) {
        displayBanner({ message: "Syncing column order...", type: "export-excel" });

        await updateUser({
          variables: {
            assetsTableSettings: currentTableSettings,
          },
        });
      }

      // Then export with the updated settings
      displayBanner({
        message: "Generating file...",
        type: "export-excel",
        projectId,
        clientId: projectId ? null : clientId || null,
        startedAt: Date.now(),
      });

      exportExcelData({
        variables: {
          ...(projectId && { projectId }),
          clientId: projectId ? null : clientId || null,
          columnsOrder: "user_table_settings",
          assetIds: selectedIds,
        },
      });
    } catch (error) {
      notifyError("Failed to sync table settings before export");
      console.error("Export error:", error);
    }
  };

  const handleExportImages = () => {
    if (!projectId) {
      notifyError("Image export requires a project");
      return;
    }

    displayBanner({
      message: "Generating file...",
      type: "download_images",
      projectId,
      startedAt: Date.now(),
    });

    downloadAssetsImages({
      variables: {
        projectId,
        assetIds: selectedIds,
      },
    });
  };

  const closeMenus = () => {
    setActiveSubmenu(null);
    popupState.close();
  };

  const openModal = (modalName) => {
    closeMenus();
    setActiveModal(modalName);
  };

  const handleCloseModal = () => setActiveModal(null);

  const handleModalComplete = () => {
    onCompleteBulk();
    setActiveModal(null);
  };

  const handleModalCompleteAndRefetch = () => {
    handleModalComplete();
    refetch();
  };

  const handleOpenOnsiteWizard = () => {
    closeMenus();
    const params = new URLSearchParams(window.location.search);
    params.set("onsiteWizard", selectedIds.join(","));
    navigate({ search: params.toString() });
  };

  const handleCloseOnsiteWizard = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete("onsiteWizard");
    params.delete("onsiteWizardStep");
    params.delete("onsiteShipmentId");
    params.delete("onsiteShipmentSid");
    navigate({ search: params.toString() });
  };

  return (
    <>
      {isSelected && (
        <>
          <Badge badgeContent={selectedCount} color="primary" max={999999}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              endIcon={
                isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <KeyboardArrowDownIcon />
                )
              }
              disabled={isLoading}
              {...bindTrigger(popupState)}
            >
              {label}
            </Button>
            <Menu {...bindMenu(popupState)}>
              {(isAdmin || can("pages.shopping_cart")) && (
                <NestedMenuItem
                  label="Add"
                  icon={<AddIcon />}
                  popupId="bulk-add"
                  activeSubmenu={activeSubmenu}
                  setActiveSubmenu={setActiveSubmenu}
                  disabled={isLoading}
                >
                  <MenuItem onClick={() => openModal("addToCart")} disabled={isLoading}>
                    <Box mr={1}><ShoppingCartIcon fontSize="small" /></Box>
                    To Shopping Cart
                  </MenuItem>
                  <MenuItem onClick={() => openModal("addToOrder")} disabled={isLoading}>
                    <Box mr={1}><AssignmentIcon fontSize="small" /></Box>
                    To Existing Outbound Order
                  </MenuItem>
                  {settings.internal_work_orders && (
                    <MenuItem onClick={() => openModal("addToInternalWorkOrder")} disabled={isLoading}>
                      <Box mr={1}><PlaylistAddIcon fontSize="small" /></Box>
                      To Existing Internal Order
                    </MenuItem>
                  )}
                  {isOnsiteCaptureToWarehouse && (
                    <MenuItem onClick={handleOpenOnsiteWizard} disabled={isLoading}>
                      <Box mr={1}><AddBoxIcon fontSize="small" /></Box>
                      To New Receiving Report
                    </MenuItem>
                  )}
                </NestedMenuItem>
              )}

              {showBulkOptions && (
                <NestedMenuItem
                  label="Move"
                  icon={<MoveToInboxIcon />}
                  popupId="bulk-move"
                  activeSubmenu={activeSubmenu}
                  setActiveSubmenu={setActiveSubmenu}
                  disabled={isLoading}
                >
                  <MenuItem onClick={() => openModal("transfer")} disabled={isLoading}>
                    <Box mr={1}><MoveToInboxIcon fontSize="small" /></Box>
                    To New Location
                  </MenuItem>
                </NestedMenuItem>
              )}

              <NestedMenuItem
                label="Transfer"
                icon={<SwapHorizIcon />}
                popupId="bulk-transfer"
                activeSubmenu={activeSubmenu}
                setActiveSubmenu={setActiveSubmenu}
                disabled={isLoading}
              >
                <MenuItem onClick={() => openModal("transferProject")} disabled={isLoading}>
                  <Box mr={1}><SwapHorizIcon fontSize="small" /></Box>
                  {`To a Different ${settings.filter_field2}`}
                </MenuItem>
              </NestedMenuItem>

              {can("features.export_assets") && (
                <NestedMenuItem
                  label="Export"
                  icon={<ExportIcon />}
                  popupId="bulk-export"
                  activeSubmenu={activeSubmenu}
                  setActiveSubmenu={setActiveSubmenu}
                  disabled={isLoading}
                >
                  <MenuItem onClick={() => { closeMenus(); handleExportExcel(); }} disabled={isLoading}>
                    <Box mr={1}><ExcelIcon fontSize="small" /></Box>
                    To Excel
                  </MenuItem>
                  <MenuItem onClick={() => { closeMenus(); handleExportImages(); }} disabled={isLoading}>
                    <Box mr={1}><ImageIcon fontSize="small" /></Box>
                    Images
                  </MenuItem>
                </NestedMenuItem>
              )}

              {(canWriteAssets || canRemoveAssets) && (
                <NestedMenuItem
                  label="Tools"
                  icon={<ToolsIcon />}
                  popupId="bulk-tools"
                  activeSubmenu={activeSubmenu}
                  setActiveSubmenu={setActiveSubmenu}
                  disabled={isLoading}
                >
                  {canWriteAssets && (
                    <MenuItem onClick={() => openModal("imageUpload")} disabled={isLoading}>
                      <Box mr={1}><ImageIcon fontSize="small" /></Box>
                      Bulk Upload Image
                    </MenuItem>
                  )}
                  {canRemoveAssets && (
                    <MenuItem onClick={() => openModal("imageRemoval")} disabled={isLoading}>
                      <Box mr={1}><DeleteSweepIcon fontSize="small" /></Box>
                      Remove All Images
                    </MenuItem>
                  )}
                  {canRemoveAssets && (
                    <MenuItem onClick={() => openModal("delete")} disabled={isLoading}>
                      <Box mr={1}><DeleteIcon fontSize="small" /></Box>
                      Delete Selected Items
                    </MenuItem>
                  )}
                </NestedMenuItem>
              )}
            </Menu>
          </Badge>
        </>
      )}

      {activeModal === "delete" && (
        <ConfirmRemoveAssetsModal
          selectedIds={bulkSelectedIds}
          title="Delete Selected Items"
          descr="Are you sure you want to delete the selected assets?"
          onClose={handleCloseModal}
          onDelete={handleBulkRemove}
        />
      )}

      {activeModal === "transfer" && (
        <BulkInventoryMoveModal
          selectedIds={bulkSelectedIds}
          onClose={handleCloseModal}
          onComplete={handleModalComplete}
        />
      )}

      {activeModal === "addToCart" && (
        <BulkAddToCartModal
          selectedIds={bulkSelectedIds}
          onClose={handleCloseModal}
          onComplete={handleModalComplete}
        />
      )}

      {activeModal === "transferProject" && (
        <BulkTransferModal
          selectedIds={bulkSelectedIds}
          onClose={handleCloseModal}
          onComplete={handleModalCompleteAndRefetch}
        />
      )}

      {activeModal === "addToOrder" && (
        <BulkAddToOrderModal
          selectedIds={bulkSelectedIds}
          onClose={handleCloseModal}
          onComplete={handleModalComplete}
        />
      )}

      {activeModal === "addToInternalWorkOrder" && (
        <BulkAddToInternalWorkOrderModal
          selectedIds={bulkSelectedIds}
          projectId={projectId}
          onClose={handleCloseModal}
          onComplete={handleModalComplete}
        />
      )}

      {activeModal === "imageUpload" && (
        <BulkImageUploadModal
          selectedIds={bulkSelectedIds}
          onClose={handleCloseModal}
          onComplete={handleModalCompleteAndRefetch}
        />
      )}

      {activeModal === "imageRemoval" && (
        <BulkImageRemovalModal
          selectedIds={bulkSelectedIds}
          onClose={handleCloseModal}
          onComplete={handleModalCompleteAndRefetch}
        />
      )}

      {isOnsiteWizardOpen && (
        <OnsiteCaptureWizard
          open={true}
          onClose={handleCloseOnsiteWizard}
          selectedIds={onsiteWizardIds}
          projectId={projectId}
          unSelectBulk={onCompleteBulk}
        />
      )}
    </>
  );
}
